import { RuntimeEnvironmentGSEnum, RuntimeGS, RuntimeScopeGS } from '../core/app/RuntimeGS';

const langStorageKey = 'gs-lang';
export const DefaultLang = 'en';
export const ServerLanguageAttribute = 'data-gs-language';

let unscopedLanguage = DefaultLang;

export function getLang(): string {
    const scope = RuntimeGS.current;
    if (!scope) return unscopedLanguage;
    if (!scope.language) scope.language = resolveScopeLanguage(scope);
    return scope.language;
}

/** Set the language for the current browser or server request scope. */
export function setLang(lang: string): void {
    const normalized = normalizeLang(lang);
    const scope = RuntimeGS.current;
    if (scope) {
        scope.language = normalized;
        if (scope.document) scope.document.documentElement.lang = normalized;
        return;
    }
    unscopedLanguage = normalized;
}

/** Set and persist the browser language without attempting to mutate existing templates. */
export function setLangAndSave(lang: string): void {
    const normalized = normalizeLang(lang);
    setLang(normalized);
    try { getStorage()?.setItem(langStorageKey, normalized); } catch { }
}

/** Persist a language and perform the full navigation required to rebuild layouts and pages. */
export function setLangAndReload(lang: string, url?: string): void {
    setLangAndSave(lang);
    const runtimeWindow = RuntimeGS.window;
    if (!runtimeWindow) return;
    if (url !== undefined) runtimeWindow.location.assign(url);
    else runtimeWindow.location.reload();
}

export function normalizeLang(lang: string): string {
    const normalized = String(lang ?? '').trim().toLowerCase();
    if (!normalized || !/^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(normalized))
        throw new Error(`Invalid language code '${lang}'.`);
    return normalized;
}

function resolveScopeLanguage(scope: RuntimeScopeGS): string {
    if (scope.environment !== RuntimeEnvironmentGSEnum.Browser) return unscopedLanguage;
    const document = scope.document;
    const candidates = [
        document?.documentElement.getAttribute(ServerLanguageAttribute),
        getSavedLanguage(),
        document?.documentElement.lang,
        unscopedLanguage
    ];
    for (const candidate of candidates) {
        if (!candidate) continue;
        try { return normalizeLang(candidate); } catch { }
    }
    return DefaultLang;
}

function getStorage(): Storage | undefined {
    try {
        return RuntimeGS.window?.localStorage;
    } catch {
        return undefined;
    }
}

function getSavedLanguage(): string | null | undefined {
    try {
        return getStorage()?.getItem(langStorageKey);
    } catch {
        return undefined;
    }
}
